import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { fetchConversations } from "@/lib/api";
import { logger } from "@/lib/logger";
import AccountSidebar from "@/components/AccountSidebar";
import PushToggle from "@/components/PushToggle";
import { PresenceDot } from "@/components/PresenceDot";
import { MessageCircle, Search } from "lucide-react";
import { withWatermark } from "@/lib/utils";

export default function MessagesPage() {
  const [convs, setConvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await fetchConversations();
      setConvs(r);
    } catch (e) {
      logger.warn("Failed to fetch conversations:", e?.message || e);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial + adaptive polling (15s visible, 60s hidden)
  useEffect(() => {
    load();
    const tick = () => {
      const delay = document.visibilityState === "visible" ? 15000 : 60000;
      timerRef.current = setTimeout(async () => {
        await load();
        tick();
      }, delay);
    };
    tick();
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [load]);

  const fmtTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const today = new Date();
    const sameDay = d.toDateString() === today.toDateString();
    if (sameDay) return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
  };

  const filtered = convs.filter((c) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (c.annonce_title || "").toLowerCase().includes(s)
      || (c.other?.name || "").toLowerCase().includes(s)
      || (c.last_message || "").toLowerCase().includes(s);
  });

  const totalUnread = convs.reduce((a, c) => a + (c.unread || 0), 0);

  return (
    <main className="max-w-7xl mx-auto px-6 py-10" data-testid="messages-page">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight">Messages</h1>
          <p className="text-[var(--ink-soft)] mt-2">
            {totalUnread > 0
              ? <span><b className="text-orange-600">{totalUnread}</b> message{totalUnread > 1 ? "s" : ""} non lu{totalUnread > 1 ? "s" : ""}</span>
              : "Vos conversations avec acheteurs et vendeurs."}
          </p>
        </div>
        <PushToggle />
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        <div className="hidden lg:block"><AccountSidebar /></div>
        <section>
          {/* Search */}
          {convs.length > 0 && (
            <div className="relative mb-4">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-soft)]" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Rechercher une conversation..."
                className="w-full pl-10 pr-4 py-2.5 rounded-full border border-[var(--line)] focus:outline-none focus:ring-2 focus:ring-orange-300 text-sm bg-white"
                data-testid="messages-search"
              />
            </div>
          )}

          {loading ? (
            <div className="glass-card p-10 text-center">Chargement...</div>
          ) : convs.length === 0 ? (
            <div className="glass-card p-12 text-center" data-testid="messages-empty">
              <MessageCircle size={36} className="mx-auto text-orange-500 mb-3" />
              <div className="font-bold text-lg mb-2">Aucune conversation</div>
              <p className="text-sm text-[var(--ink-soft)] mb-5">Contactez un vendeur depuis une annonce pour demarrer une discussion.</p>
              <Link to="/annonces" className="btn-primary">Voir les annonces</Link>
            </div>
          ) : filtered.length === 0 ? (
            <div className="glass-card p-10 text-center text-[var(--ink-soft)]">Aucun resultat pour &laquo;{q}&raquo;</div>
          ) : (
            <div className="space-y-2">
              {filtered.map((c) => {
                const unread = c.unread || 0;
                return (
                  <Link
                    key={c.id}
                    to={`/messages/${c.id}`}
                    className={`glass-card p-3.5 flex items-center gap-3 hover:translate-y-[-1px] transition-all ${
                      unread > 0 ? "ring-2 ring-orange-200 bg-orange-50/40" : ""
                    }`}
                    data-testid={`conv-${c.id}`}
                  >
                    <div className="relative">
                      <img
                        src={withWatermark(c.annonce_image) || "/placeholder.png"}
                        className="w-14 h-14 rounded-2xl object-cover bg-orange-50"
                        alt=""
                      />
                      {c.other?.picture && (
                        <img
                          src={c.other.picture}
                          alt=""
                          className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full ring-2 ring-white object-cover"
                        />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-baseline gap-3">
                        <div className={`truncate ${unread > 0 ? "font-extrabold" : "font-bold"}`}>{c.annonce_title}</div>
                        <div className={`text-xs whitespace-nowrap ${unread > 0 ? "text-orange-600 font-bold" : "text-[var(--ink-soft)]"}`}>
                          {fmtTime(c.last_at)}
                        </div>
                      </div>
                      <div className="text-xs text-[var(--ink-soft)] mt-0.5 truncate flex items-center gap-2">
                        <span className="truncate">Avec <b>{c.other?.name || "Vendeur"}</b></span>
                        {c.other?.user_id && <PresenceDot userId={c.other.user_id} showLabel={false} />}
                      </div>
                      <div className="flex items-center justify-between gap-2 mt-1">
                        <div className={`text-sm truncate ${unread > 0 ? "text-[var(--ink)] font-semibold" : "text-[var(--ink-soft)]"}`}>
                          {c.last_message || "..."}
                        </div>
                        {unread > 0 && (
                          <span
                            className="min-w-[20px] h-5 px-1.5 rounded-full bg-orange-500 text-white text-[11px] font-bold grid place-items-center shrink-0"
                            data-testid={`conv-${c.id}-unread`}
                          >
                            {unread > 99 ? "99+" : unread}
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
